import { useEffect, useState } from 'react'

export type DocumentTheme = 'light' | 'dark'

/**
 * The light/dark theme currently applied to the document.
 *
 * Read from the DOM rather than from a React state holder on purpose. The
 * switch is owned by ThemeToggle in @profylr/ui, which writes both
 * `data-theme` and the `dark` class to the root element and keeps its own
 * state. Calling that package's `useTheme` a second time here would create a
 * second, independent copy of that state -- flipping the toggle would update
 * one and not the other, and the sidebar would keep the colours of whichever
 * theme it was mounted in.
 *
 * Observing the element both hooks write to sidesteps that entirely, and works
 * for anything else that ever sets the theme.
 */
function readDocumentTheme(): DocumentTheme {
  if (typeof document === 'undefined') return 'light'

  const root = document.documentElement
  return root.dataset.theme === 'dark' || root.classList.contains('dark') ? 'dark' : 'light'
}

export function useDocumentTheme(): DocumentTheme {
  const [theme, setTheme] = useState<DocumentTheme>(readDocumentTheme)

  useEffect(() => {
    const sync = () => setTheme(readDocumentTheme())

    // The toggle may have run between the initial render and this effect.
    sync()

    const observer = new MutationObserver(sync)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    })

    return () => observer.disconnect()
  }, [])

  return theme
}
